
import { SvcType } from "@/lib/utils";
import type {
  HotelTempBookingSnapshot,
} from "@/types/booking/checkout";
import { useMemo } from "react";

export type HotelCheckoutQueryParams = {
  start: string;
  end: string;
  adults: number;
  children: number;
  rooms: number;
};

type HotelCheckoutContextParams = {
  hotelId: string;
  uniqueId: string;
  start: string;
  end: string;
  adults: number;
  children: number;
  selectedQtyByRoomId: Record<number, number>;
};

const normalizeRoomSelection = (selectedQtyByRoomId: Record<number, number>) =>
  Object.entries(selectedQtyByRoomId)
    .filter(([, quantity]) => quantity > 0)
    .sort(
      ([firstRoomId], [secondRoomId]) =>
        Number(firstRoomId) - Number(secondRoomId),
    );

export const isHotelTempBookingSnapshot = (
  value: unknown,
): value is HotelTempBookingSnapshot => {
  if (!value || typeof value !== "object") {
    return false;
  }

  const snapshot = value as Partial<HotelTempBookingSnapshot>;
  return Boolean(
    snapshot.booking &&
    typeof snapshot.booking.start === "string" &&
    typeof snapshot.booking.end === "string" &&
    snapshot.hotel &&
    typeof snapshot.hotel.id === "number" &&
    Array.isArray(snapshot.rooms),
  );
};

export const buildHotelCheckoutContextKey = ({
  hotelId,
  uniqueId,
  start,
  end,
  adults,
  children,
  selectedQtyByRoomId,
}: HotelCheckoutContextParams) => {
  const roomSelection = normalizeRoomSelection(selectedQtyByRoomId);

  if (roomSelection.length === 0) {
    return null;
  }

  return JSON.stringify({
    hotelId,
    uniqueId,
    start,
    end,
    adults,
    children,
    roomSelection,
  });
};

type OrderSummaryItemInput = {
  id: number | string;
  name: string;
  quantity: number;
  price: number;
  salePrice: number;
};

export const toOrderSummaryItems = (
  items: OrderSummaryItemInput[],
  stayNights?: number,
) => {
  return items.map((item) => toOrderSummaryItem(item, stayNights));
};

export const getDurationLabel = (
  duration: number = 1,
  svcType: SvcType = "hotel",
) => {
  const count = duration > 0 ? duration : 1;

  switch (svcType) {
    case "rental":
      return `${count} Day${count > 1 ? "s" : ""}`;
    case "tour_act":
      return `${count} Day${count > 1 ? "s" : ""}`;
    case "spa_msg":
      return `${count} Session${count > 1 ? "s" : ""}`;
    case "hotel":
    default:
      return `${count} Night${count > 1 ? "s" : ""}`;
  }
};

export const toOrderSummaryItem = (
  item: OrderSummaryItemInput,
  duration?: number,
  svcType: SvcType = "hotel",
) => {
  return {
    id: item.id,
    name: item.name,
    quantity: item.quantity,
    durationLabel: getDurationLabel(duration, svcType),
    price: item.price * item.quantity,
    salePrice: item.salePrice * item.quantity,
  };
};

export const getStayNightsLabel = (stayNights: number) =>
  `${stayNights} Night${stayNights > 1 ? "s" : ""}`;
